import { poseJoints } from "../../articulation/poseJoints";
import { BabylonState } from "../../state";
import { executeMeasurePin } from "../picking/executeMeasurePin";
import { reportMeasureOverlay } from "../picking/reportMeasureOverlay";
import { reportToolHover } from "../picking/reportToolHover";
import { reportDataLoading } from "../reloadStaleComponents/reportDataLoading";

import { applyRenderOrigin } from "./applyRenderOrigin";
import { constrainCamera } from "./constrainCamera";
import { renderMeasures } from "./renderMeasures";
import { resolveGlobalPositions } from "./resolveGlobalPositions";
import { updatePositions } from "./setPositionsToTime";
import { updateRotations } from "./setRotationsToTime";
import { updateVectorFields } from "./setVectorFieldsToTime";
import { updateCamera } from "./updateCamera";

/**
 * Renders the babylon scene to its canvas.
 *
 * Float64-source data is converted to float32 in the graphics pipeline,
 * which loses precision far from the origin. Each frame translates the
 * data-space focus point to render-space `(0,0,0)` so the visible
 * region stays inside float32's precise range. System order keeps that
 * mapping coherent:
 *   1. `updatePositions` / `updateRotations` / `updateVectorFields`
 *      populate each entity's *local* data-space transform for the
 *      current time — parent-relative for a child, absolute for a root.
 *   2. `poseJoints` overrides that local orientation for any joint
 *      something is driving live, so what composes below is the pose the
 *      user is holding rather than the one the spec declares.
 *   3. `resolveGlobalPositions` accumulates those up each parent chain
 *      into absolute positions, in float64.
 *   4. `updateCamera` writes `camera.focusPoint` from per-mode sources
 *      — orbit copies the tracked entity's *absolute* position,
 *      third-person consumes the input accumulator.
 *   5. `applyRenderOrigin` translates every mesh by `-camera.focusPoint`.
 *   6. `renderMeasures` places the measure tool's lines and dots at
 *      `point - camera.focusPoint`, the same render space step 5 put
 *      every other mesh in.
 *   7. `constrainCamera` clamps zoom, draw distance, and fog.
 *   8. `scene.render(false)` rasterizes; `false` skips Babylon's own
 *      `camera.update()`, which step 7 already ran.
 *   9. `reportToolHover` picks against the world matrices step 8 wrote,
 *      and `reportMeasureOverlay` projects through
 *      `scene.getTransformMatrix()`, which holds this frame's
 *      view-projection only after step 8. `executeMeasurePin` picks
 *      nothing: the pointer handler already resolved each click against
 *      the frame it landed on, and this step only folds the queued
 *      points into the measure session.
 *  10. `reportDataLoading` reports whether any entity's data is still
 *      fetching. The reload poll already reports a load the moment it
 *      starts; this per-frame call is what notices one ending, since loads
 *      finish in the background between frames. It reads only load states,
 *      so its place in the frame does not matter.
 *
 * A pin lands in step 9 but is drawn by step 6, so a new measurement's
 * geometry appears on the following frame. The loop is unconditional
 * (`engine.runRenderLoop`), so that is one frame's delay, not a wait for
 * the next input.
 *
 * Steps 3 and 5 are the whole reason the hierarchy is composed in JS rather
 * than through Babylon parenting: the accumulation happens at full float64
 * precision, and only the small post-origin remainder reaches the float32
 * graphics pipeline.
 */
export function render(state: BabylonState) {
  updatePositions(state);
  updateRotations(state);
  updateVectorFields(state);
  poseJoints(state);
  resolveGlobalPositions(state);
  updateCamera(state);
  applyRenderOrigin(state);
  renderMeasures(state);
  constrainCamera(state);

  state.context.scene.render(false);
  executeMeasurePin(state);
  reportToolHover(state);
  reportMeasureOverlay(state);
  reportDataLoading(state);
}
